/**
 * BusinessGraphRenderer.tsx - Strategic Intelligence Visualization
 *
 * Renders an interactive Cytoscape.js graph of the Biz_Ontology (110 nodes)
 * with exploration highlighting based on OMAP analysis context.
 *
 * Features:
 * - Fixed positions from graph.json
 * - Node shapes, sizes, and fonts from metadata
 * - Always-visible edge connections
 * - Exploration highlighting (explored vs unexplored nodes)
 * - Anchor node highlighting
 * - Filter controls (All/Related/Unrelated/Company Info)
 * - Reset button with zoom indicator
 * - Single-click to select, double-click to edit
 *
 * @see BUSINESS_GRAPH_TAB_IMPLEMENTATION_PLAN.md
 */

import { useEffect, useLayoutEffect, useRef, useCallback, useMemo, useState } from 'react'
// @ts-ignore - react-cytoscapejs doesn't have type declarations
import CytoscapeComponent from 'react-cytoscapejs'
import cytoscape from 'cytoscape'
import type { Core, NodeSingular } from 'cytoscape'
// @ts-ignore - cytoscape-svg doesn't have type declarations
import cytoscapeSvg from 'cytoscape-svg'

// Register cytoscape-svg extension (provides cy.svg() method)
if (typeof cytoscape('core', 'svg') !== 'function') {
  cytoscape.use(cytoscapeSvg)
}
import { useAppSelector, useAppDispatch } from '@state/hooks'
import { useTheme } from '@contexts/ThemeContext'
import { themeColors } from '@config/themeColors'
import { loadGraphData, setFilterMode, setSelectedNode, toggleCausalChainOverlay, saveViewport } from '@state/slices/graphSlice'
import type { GraphFilterMode, GraphNode, GraphEdge, CausalChainEdge } from '@models/index'
import { companyProfileApi } from '@services/companyProfileApi'
import { getNumericCompanyId } from '@utils/companyUtils'
import { NodeEditorModal } from '../editors/NodeEditorModal'
import './BusinessGraphRenderer.css'

// Node color palette
const NODE_COLORS = {
  anchor: '#bbdefb',      // Blue for anchor nodes
  explored: '#a3d5a6',    // Green for explored nodes
  unexplored: '#e8e8e8',  // Light gray for unexplored nodes
  external: '#fff3e0',    // Orange for external (PESTLE) nodes
}

const BORDER_COLORS = {
  anchor: '#1976d2',
  explored: '#2e7d32',
  unexplored: '#999999',
  external: '#ff9800',
}

// Map graph.json shape_type to Cytoscape shapes
const SHAPE_MAP: Record<string, string> = {
  'ellipse': 'ellipse',
  'rectangle': 'rectangle',
  'diamond': 'diamond',
  'triangle': 'triangle',
  'round-rectangle': 'round-rectangle',
  'hexagon': 'hexagon',
  'octagon': 'octagon',
  'star': 'star',
}

// External node patterns (PESTLE + Five Forces)
const EXTERNAL_PATTERNS = [
  'political', 'economic', 'social', 'technological',
  'environmental', 'legal', 'macro_env',
  'competitor', 'new_entrant', 'substitute', 'supplier', 'buyer'
]

// Company Name node meta_id for reset centering
const COMPANY_NAME_NODE_ID = 'company_id'

function isExternalNode(nodeId: string): boolean {
  const nodeLower = nodeId.toLowerCase()
  return EXTERNAL_PATTERNS.some(pattern => nodeLower.includes(pattern))
}

function getNodeVisualState(
  metaId: string,
  anchorIds: string[],
  exploredIds: string[]
): 'anchor' | 'explored' | 'unexplored' | 'external' {
  if (anchorIds.includes(metaId)) return 'anchor'
  if (exploredIds.includes(metaId)) return 'explored'
  if (isExternalNode(metaId)) return 'external'
  return 'unexplored'
}

interface BusinessGraphRendererProps {
  height?: number
  showFilters?: boolean
  showStats?: boolean
  showInsights?: boolean
  autoZoom?: boolean
}

export function BusinessGraphRenderer({
  height = 600,
  showFilters = true,
  showStats = true,
  showInsights = true,
  autoZoom = false
}: BusinessGraphRendererProps) {
  const dispatch = useAppDispatch()
  const { theme } = useTheme()
  const graphColors = themeColors[theme].graph
  const cyRef = useRef<Core | null>(null)

  // Redux state
  const { data: graphData, loading, error, viewport: savedViewport } = useAppSelector(state => state.graph)
  const { anchorIds, exploredIds, filterMode, confidenceScore, taxonomyDistribution, zoomCenter, insights, causalChainVisualization, showCausalChain } = useAppSelector(state => state.graph.exploration)
  const selectedNodeId = useAppSelector(state => state.graph.selectedNodeId)

  // Track if initial viewport setup is complete (state-based so selectedNodeId effect re-runs when it flips)
  const [initialSetupDone, setInitialSetupDone] = useState(false)

  // Company Info mode state
  const [populatedNodes, setPopulatedNodes] = useState<Set<string>>(new Set())
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingNode, setEditingNode] = useState<{
    id: string
    label: string
    description?: string
  } | null>(null)

  // Company-specific node data for info panel
  const [companyNodeValue, setCompanyNodeValue] = useState<string | null>(null)
  const [companyNodeLoading, setCompanyNodeLoading] = useState(false)

  // Insights panel state
  const [insightsExpanded, setInsightsExpanded] = useState(false)

  // Fullscreen state for graph expand
  const [isFullscreen, setIsFullscreen] = useState(false)

  // No causal chain alert state
  const [showNoCausalChainAlert, setShowNoCausalChainAlert] = useState(false)

  // Provenance path highlighting state (Phase F)
  const [highlightedPath, setHighlightedPath] = useState<string[]>([])

  // Zoom level tracking
  const [zoomLevel, setZoomLevel] = useState(100)

  // Track if initial fit has been done (prevent re-fitting on re-renders)
  const initialFitDone = useRef(false)

  // Track latest viewport state synchronously (no debounce lag).
  // Updated on every scroll/zoom/pan event so unmount cleanup always has fresh data,
  // even when DOM is already removed during useEffect cleanup phase.
  const viewportRef = useRef<{
    zoom: number
    pan: { x: number; y: number }
    scrollLeft: number
    scrollTop: number
  } | null>(null)

  // Track if Cytoscape core is ready (for effects that need cy instance)
  const [cyReady, setCyReady] = useState(false)

  // Graph canvas dimensions (computed from node bounds)
  const [canvasSize, setCanvasSize] = useState({ width: 2000, height: 1500 })

  // Graph coordinate offset (to handle negative coordinates)
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 })

  // Read company ID from Redux state
  const { activeCompanyId } = useAppSelector(state => state.companies)
  const companyId = getNumericCompanyId(activeCompanyId)

  // Build meta_id to shape_id mapping
  const metaIdToShapeId = useMemo(() => {
    if (!graphData) return new Map<string, string>()
    const map = new Map<string, string>()
    graphData.nodes.forEach((node: GraphNode) => {
      map.set(node.meta_id, node.id)
    })
    return map
  }, [graphData])

  // Load graph data on mount
  useEffect(() => {
    if (!graphData) {
      dispatch(loadGraphData())
    }
  }, [dispatch, graphData])

  // Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false)
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isFullscreen])

  // Calculate canvas size from graph node bounds
  useEffect(() => {
    if (!graphData || !graphData.nodes || graphData.nodes.length === 0) return

    let minX = Infinity, maxX = -Infinity
    let minY = Infinity, maxY = -Infinity

    graphData.nodes.forEach((node: GraphNode) => {
      const nodeWidth = node.width || 60
      const nodeHeight = node.height || 60
      minX = Math.min(minX, node.x - nodeWidth / 2)
      maxX = Math.max(maxX, node.x + nodeWidth / 2)
      minY = Math.min(minY, node.y - nodeHeight / 2)
      maxY = Math.max(maxY, node.y + nodeHeight / 2)
    })

    // Add padding around the graph
    const padding = 100

    // Calculate actual extent needed (accounting for negative coordinates)
    const graphWidth = maxX - minX + padding * 2
    const graphHeight = maxY - minY + padding * 2

    // Canvas size must accommodate max zoom (3.0 = 300%)
    // This ensures scroll range is sufficient at all zoom levels
    const maxZoomFactor = 3.0
    const width = Math.max(1200, graphWidth * maxZoomFactor)
    const height = Math.max(800, graphHeight * maxZoomFactor)

    // Calculate offset to translate Cytoscape so minX,minY maps to (padding, padding)
    // This ensures scrolling to (0,0) shows the leftmost/topmost nodes
    const offsetX = -minX + padding
    const offsetY = -minY + padding

    setCanvasSize({ width, height })
    setCanvasOffset({ x: offsetX, y: offsetY })
    console.log('[BusinessGraph] Canvas size calculated:', { width, height, minX, maxX, minY, maxY, offsetX, offsetY })
  }, [graphData])

  // Load populated nodes on mount and when companyId changes (always, not just company_info mode)
  useEffect(() => {
    loadPopulatedNodes()
  }, [companyId])

  // Fetch company-specific data when a node is selected (for info panel)
  useEffect(() => {
    if (!selectedNodeId || !populatedNodes.has(selectedNodeId)) {
      setCompanyNodeValue(null)
      return
    }
    let cancelled = false
    setCompanyNodeLoading(true)
    companyProfileApi.getNodeValue(companyId, selectedNodeId)
      .then(result => {
        if (!cancelled) {
          setCompanyNodeValue(result.node_value || null)
        }
      })
      .catch(() => {
        if (!cancelled) setCompanyNodeValue(null)
      })
      .finally(() => {
        if (!cancelled) setCompanyNodeLoading(false)
      })
    return () => { cancelled = true }
  }, [selectedNodeId, companyId, populatedNodes])

  // Auto-expand insights when insights become available
  useEffect(() => {
    if (insights && (Object.keys(insights.blind_spots || {}).length > 0 || insights.cross_pillar_dependencies?.length > 0)) {
      setInsightsExpanded(true)
    }
  }, [insights])

  // Zoom in handler (for + button)
  const handleZoomIn = useCallback(() => {
    if (!cyRef.current) return
    const cy = cyRef.current
    const currentZoom = cy.zoom()
    const newZoom = Math.min(3, currentZoom * 1.2)

    // Zoom centered on current viewport center
    const scrollWrapper = document.querySelector('.cytoscape-scroll-wrapper')
    if (scrollWrapper) {
      const centerX = scrollWrapper.scrollLeft + scrollWrapper.clientWidth / 2
      const centerY = scrollWrapper.scrollTop + scrollWrapper.clientHeight / 2

      const pan = cy.pan()
      const modelX = (centerX - pan.x) / currentZoom
      const modelY = (centerY - pan.y) / currentZoom

      cy.zoom(newZoom)

      const newPanX = centerX - modelX * newZoom
      const newPanY = centerY - modelY * newZoom
      cy.pan({ x: newPanX, y: newPanY })
    } else {
      cy.zoom(newZoom)
    }

    setZoomLevel(Math.round(newZoom * 100))
  }, [])

  // Zoom out handler (for - button)
  const handleZoomOut = useCallback(() => {
    if (!cyRef.current) return
    const cy = cyRef.current
    const currentZoom = cy.zoom()
    const newZoom = Math.max(0.5, currentZoom / 1.2)

    // Zoom centered on current viewport center
    const scrollWrapper = document.querySelector('.cytoscape-scroll-wrapper')
    if (scrollWrapper) {
      const centerX = scrollWrapper.scrollLeft + scrollWrapper.clientWidth / 2
      const centerY = scrollWrapper.scrollTop + scrollWrapper.clientHeight / 2

      const pan = cy.pan()
      const modelX = (centerX - pan.x) / currentZoom
      const modelY = (centerY - pan.y) / currentZoom

      cy.zoom(newZoom)

      const newPanX = centerX - modelX * newZoom
      const newPanY = centerY - modelY * newZoom
      cy.pan({ x: newPanX, y: newPanY })
    } else {
      cy.zoom(newZoom)
    }

    setZoomLevel(Math.round(newZoom * 100))
  }, [])

  // Initial view setup - runs AFTER both Cytoscape is ready AND canvasOffset is calculated
  // This fixes the timing issue where handleCyReady ran before canvasOffset was available
  useEffect(() => {
    // Wait for Cytoscape to be ready
    if (!cyReady || !cyRef.current) return

    // Wait for canvas offset to be calculated (non-default value)
    // The canvas offset effect sets this after calculating from graphData
    if (canvasOffset.x === 0 && canvasOffset.y === 0) return

    // Only run initial setup once
    if (initialFitDone.current) return
    initialFitDone.current = true
    const cy = cyRef.current

    if (!savedViewport) {
      // Path A: No saved viewport - apply default view (Reset settings)
      console.log('[BusinessGraph] Applying default view with canvasOffset:', canvasOffset)
      cy.zoom(1.0)
      cy.pan({ x: canvasOffset.x, y: canvasOffset.y })
      setZoomLevel(100)

      // Scroll to Company Name node
      setTimeout(() => {
        const scrollWrapper = document.querySelector('.cytoscape-scroll-wrapper')
        if (scrollWrapper) {
          const companyNodeShapeId = metaIdToShapeId.get(COMPANY_NAME_NODE_ID)
          if (companyNodeShapeId) {
            const companyNode = cy.getElementById(companyNodeShapeId)
            if (companyNode.length > 0) {
              const pos = companyNode.position()
              const scrollLeft = pos.x + canvasOffset.x - scrollWrapper.clientWidth / 2
              const scrollTop = pos.y + canvasOffset.y - scrollWrapper.clientHeight / 2
              scrollWrapper.scrollTo({
                left: Math.max(0, scrollLeft),
                top: Math.max(0, scrollTop),
                behavior: 'auto'
              })
              console.log('[BusinessGraph] Scrolled to Company Name node:', { scrollLeft, scrollTop })
            }
          }
        }
        setInitialSetupDone(true)
      }, 50)
    } else {
      // Path B: Saved viewport exists - restore it
      console.log('[BusinessGraph] Restoring saved viewport:', savedViewport)
      cy.zoom(savedViewport.zoom)
      // CRITICAL: Clone the pan object - Redux state is frozen/immutable
      // If we pass the frozen object directly, Cytoscape will store it and later
      // fail when trying to modify pan during zoom or drag operations
      cy.pan({ ...savedViewport.pan })
      setZoomLevel(Math.round(savedViewport.zoom * 100))

      // Restore scroll position
      setTimeout(() => {
        const scrollWrapper = document.querySelector('.cytoscape-scroll-wrapper')
        if (scrollWrapper) {
          scrollWrapper.scrollLeft = savedViewport.scrollLeft
          scrollWrapper.scrollTop = savedViewport.scrollTop
        }
        setInitialSetupDone(true)
      }, 50)
    }
  }, [cyReady, canvasOffset, savedViewport, metaIdToShapeId])

  // Save viewport state on unmount using useLayoutEffect.
  // useLayoutEffect cleanup runs BEFORE DOM removal (Before Mutation phase),
  // unlike useEffect cleanup which runs AFTER DOM removal (Passive Effects phase).
  // This is critical because document.querySelector('.cytoscape-scroll-wrapper') returns
  // null after DOM removal, causing scrollLeft/scrollTop to save as 0.
  useLayoutEffect(() => {
    return () => {
      if (!initialFitDone.current) return

      // Primary: read from synchronously-updated ref (always latest, no DOM dependency)
      if (viewportRef.current) {
        dispatch(saveViewport({ ...viewportRef.current }))
        dispatch(setSelectedNode(null))
        console.log('[BusinessGraph] Saved viewport on unmount (from ref):', viewportRef.current)
        return
      }

      // Fallback: read from DOM/cy (useLayoutEffect runs before DOM removal)
      if (cyRef.current) {
        const cy = cyRef.current
        const scrollWrapper = document.querySelector('.cytoscape-scroll-wrapper')
        const viewportState = {
          zoom: cy.zoom(),
          pan: { ...cy.pan() },
          scrollLeft: scrollWrapper?.scrollLeft || 0,
          scrollTop: scrollWrapper?.scrollTop || 0,
        }
        dispatch(saveViewport(viewportState))
        dispatch(setSelectedNode(null))
        console.log('[BusinessGraph] Saved viewport on unmount (from DOM):', viewportState)
      }
    }
  }, [dispatch])

  // CRITICAL: Save viewport proactively on scroll/zoom changes.
  // Two-tier strategy:
  // 1. viewportRef updated SYNCHRONOUSLY on every event (zero debounce) —
  //    ensures unmount cleanup always has absolute latest values.
  // 2. Redux dispatch remains debounced (150ms) to avoid excessive updates.
  useEffect(() => {
    if (!cyReady || !cyRef.current) return

    const scrollWrapper = document.querySelector('.cytoscape-scroll-wrapper')
    if (!scrollWrapper) return

    let saveTimeout: ReturnType<typeof setTimeout> | null = null

    const handleViewportChange = () => {
      if (!cyRef.current || !initialFitDone.current) return
      const cy = cyRef.current

      // SYNCHRONOUS: Update ref immediately on every event
      viewportRef.current = {
        zoom: cy.zoom(),
        pan: { ...cy.pan() },
        scrollLeft: scrollWrapper.scrollLeft,
        scrollTop: scrollWrapper.scrollTop,
      }

      // DEBOUNCED: Dispatch to Redux (150ms)
      if (saveTimeout) clearTimeout(saveTimeout)
      saveTimeout = setTimeout(() => {
        if (viewportRef.current) {
          dispatch(saveViewport({ ...viewportRef.current }))
        }
      }, 150)
    }

    // Save on scroll (user pans via scroll bars or drag)
    scrollWrapper.addEventListener('scroll', handleViewportChange)

    // Save on zoom changes (via cy event)
    const cy = cyRef.current
    cy.on('zoom', handleViewportChange)
    cy.on('pan', handleViewportChange)

    return () => {
      if (saveTimeout) clearTimeout(saveTimeout)
      scrollWrapper.removeEventListener('scroll', handleViewportChange)
      cy.off('zoom', handleViewportChange)
      cy.off('pan', handleViewportChange)
    }
  }, [dispatch, cyReady])

  // Auto-zoom when zoomCenter changes (Quick Mode response received)
  useEffect(() => {
    if (autoZoom && cyRef.current && zoomCenter && zoomCenter.x !== undefined && zoomCenter.y !== undefined) {
      const cy = cyRef.current
      cy.animate({
        center: { eles: cy.nodes().filter((n) => anchorIds.some(a => metaIdToShapeId.get(a) === n.id())) },
        zoom: 1.2,
        duration: 500,
        easing: 'ease-out-cubic'
      })
    }
  }, [zoomCenter, autoZoom, anchorIds, metaIdToShapeId])

  // Provenance path highlighting via Cytoscape class API (avoids recomputing elements)
  useEffect(() => {
    if (!cyRef.current) return
    const cy = cyRef.current

    // Remove provenance-path from all edges first
    cy.edges('.provenance-path').removeClass('provenance-path').addClass('base-edge')

    if (highlightedPath.length > 1) {
      // Find edges that match consecutive pairs in the path
      cy.edges().forEach((edge) => {
        const srcMeta = edge.data('sourceMetaId')
        const tgtMeta = edge.data('targetMetaId')
        if (!srcMeta || !tgtMeta) return

        const isOnPath = highlightedPath.some((metaId, idx) => {
          if (idx === highlightedPath.length - 1) return false
          const nextMetaId = highlightedPath[idx + 1]
          return (srcMeta === metaId && tgtMeta === nextMetaId) ||
                 (tgtMeta === metaId && srcMeta === nextMetaId)
        })

        if (isOnPath) {
          edge.removeClass('base-edge')
          edge.addClass('provenance-path')
        }
      })
    }
  }, [highlightedPath])

  // Phase 3: Center and highlight node when selectedNodeId changes (Evidence Navigation)
  // Uses scroll-wrapper centering (not cy.animate) to maintain scroll-Cytoscape sync
  useEffect(() => {
    if (!cyRef.current || !selectedNodeId || !initialSetupDone) return

    const cy = cyRef.current

    // Find the Cytoscape node that matches the selected meta_id
    const shapeId = metaIdToShapeId.get(selectedNodeId)
    if (!shapeId) {
      console.log('[BusinessGraphRenderer] No shape ID found for meta_id:', selectedNodeId)
      return
    }

    const targetNode = cy.getElementById(shapeId)
    if (!targetNode || targetNode.empty()) {
      console.log('[BusinessGraphRenderer] Node not found in Cytoscape:', shapeId)
      return
    }

    // Unselect all nodes first
    cy.nodes().unselect()

    // Select the target node
    targetNode.select()

    // Scroll to center on the selected node (maintains current zoom, no cy.animate)
    const scrollWrapper = document.querySelector('.cytoscape-scroll-wrapper')
    if (scrollWrapper) {
      const pos = targetNode.position()
      const currentZoom = cy.zoom()
      const pan = cy.pan()
      const scrollLeft = pos.x * currentZoom + pan.x - scrollWrapper.clientWidth / 2
      const scrollTop = pos.y * currentZoom + pan.y - scrollWrapper.clientHeight / 2
      scrollWrapper.scrollTo({
        left: Math.max(0, scrollLeft),
        top: Math.max(0, scrollTop),
        behavior: 'smooth'
      })
    }

    console.log('[BusinessGraphRenderer] Scrolled to selected node:', selectedNodeId)
  }, [selectedNodeId, metaIdToShapeId, initialSetupDone])

  // Re-apply Cytoscape stylesheet when theme changes
  useEffect(() => {
    if (!cyRef.current) return
    const cy = cyRef.current as any
    cy.style().fromJson(stylesheet).update()
  }, [theme]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadPopulatedNodes = async () => {
    try {
      const response = await companyProfileApi.getPopulatedNodes(companyId)
      setPopulatedNodes(new Set(response.node_ids))
    } catch (err) {
      console.error('[BusinessGraphRenderer] Error loading populated nodes:', err)
    }
  }

  // Handle node value saved - refresh populated nodes badge
  const handleNodeSaved = useCallback((nodeId: string, hasValue: boolean) => {
    setPopulatedNodes(prev => {
      const next = new Set(prev)
      if (hasValue) {
        next.add(nodeId)
      } else {
        next.delete(nodeId)
      }
      return next
    })
  }, [])

  // Build Cytoscape elements from graph data
  const elements = useMemo(() => {
    if (!graphData) return []

    const nodes = graphData.nodes
      .filter((node: GraphNode) => {
        // Apply filter mode using meta_id
        const isExplored = anchorIds.includes(node.meta_id) || exploredIds.includes(node.meta_id)
        if (filterMode === 'related' && !isExplored) return false
        if (filterMode === 'unrelated' && isExplored) return false
        return true
      })
      .map((node: GraphNode) => {
        const state = getNodeVisualState(node.meta_id, anchorIds, exploredIds)
        const isAnchor = state === 'anchor'
        const hasData = populatedNodes.has(node.meta_id)

        // Build classes string
        let classes = state
        if (hasData) {
          classes += ' has-data'
        }

        // Get shape from metadata, default to ellipse
        const shape = SHAPE_MAP[node.shape_type] || 'ellipse'

        return {
          data: {
            id: node.id,  // Use shape ID so edges connect properly
            metaId: node.meta_id,  // Store meta_id for exploration lookup
            label: node.label,
            fullLabel: node.label,
            description: node.meta_description,
            state,
            isAnchor,
            hasData,
            // Store dimensions for stylesheet
            nodeWidth: node.width || 60,
            nodeHeight: node.height || 60,
            fontSize: node.font_size || 10,
            shape,
          },
          position: { x: node.x, y: node.y },
          classes,
        }
      })

    const visibleNodeIds = new Set(nodes.map((n: any) => n.data.id))

    // Always include edges where both nodes are visible
    const edges = (graphData.edges || [])
      .filter((edge: GraphEdge) =>
        visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
      )
      .map((edge: GraphEdge) => {
        // Find source and target meta_ids for exploration check
        const sourceNode = graphData.nodes.find((n: GraphNode) => n.id === edge.source)
        const targetNode = graphData.nodes.find((n: GraphNode) => n.id === edge.target)
        const sourceMetaId = sourceNode?.meta_id || ''
        const targetMetaId = targetNode?.meta_id || ''

        return {
          data: {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            label: edge.label,
            sourceMetaId,
            targetMetaId,
          },
          classes: 'base-edge',
        }
      })

    // Add causal chain edges as overlay (Phase F)
    const causalChainEdges: any[] = []
    if (showCausalChain && causalChainVisualization?.edges) {
      causalChainVisualization.edges.forEach((ccEdge: CausalChainEdge, idx: number) => {
        // Convert meta_ids to shape_ids
        const sourceShapeId = metaIdToShapeId.get(ccEdge.source_id)
        const targetShapeId = metaIdToShapeId.get(ccEdge.target_id)

        if (sourceShapeId && targetShapeId && visibleNodeIds.has(sourceShapeId) && visibleNodeIds.has(targetShapeId)) {
          causalChainEdges.push({
            data: {
              id: `causal-${idx}`,
              source: sourceShapeId,
              target: targetShapeId,
              label: ccEdge.relationship,
              sourcePerspective: ccEdge.source_perspective,
              targetPerspective: ccEdge.target_perspective,
            },
            classes: 'causal-chain-edge',
          })
        }
      })
    }

    return [...nodes, ...edges, ...causalChainEdges]
  }, [graphData, anchorIds, exploredIds, filterMode, populatedNodes, showCausalChain, causalChainVisualization, metaIdToShapeId])

  // Cytoscape stylesheet - black text, no outline, white edges
  const stylesheet: any[] = [
    // Base node style with dynamic dimensions from data
    {
      selector: 'node',
      style: {
        'background-color': NODE_COLORS.unexplored,
        'border-color': graphColors.nodeBorder,
        'border-width': 1,
        'label': 'data(label)',
        'font-size': 'data(fontSize)',
        'text-valign': 'center',
        'text-halign': 'center',
        'text-wrap': 'wrap',
        'text-max-width': 'data(nodeWidth)',
        'width': 'data(nodeWidth)',
        'height': 'data(nodeHeight)',
        'shape': 'data(shape)',
        'opacity': 0.7,
        'color': graphColors.nodeText,
      },
    },
    // Anchor nodes
    {
      selector: 'node.anchor',
      style: {
        'background-color': NODE_COLORS.anchor,
        'border-color': BORDER_COLORS.anchor,
        'border-width': 3,
        'opacity': 1,
        'font-weight': 'bold',
      },
    },
    // Explored nodes
    {
      selector: 'node.explored',
      style: {
        'background-color': NODE_COLORS.explored,
        'border-color': BORDER_COLORS.explored,
        'border-width': 2,
        'opacity': 1,
      },
    },
    // External nodes (PESTLE/Five Forces)
    {
      selector: 'node.external',
      style: {
        'background-color': NODE_COLORS.external,
        'border-color': BORDER_COLORS.external,
        'border-width': 1,
        'opacity': 0.85,
      },
    },
    // Selected node
    {
      selector: 'node:selected',
      style: {
        'border-color': '#1976d2',
        'border-width': 4,
        'overlay-opacity': 0.2,
        'overlay-color': '#1976d2',
      },
    },
    // Nodes with company data (green fill in all modes)
    {
      selector: 'node.has-data',
      style: {
        'background-color': '#c8e6c9',
        'border-color': '#2e7d32',
        'border-width': 3,
        'opacity': 1,
      },
    },
    // Base edge style
    {
      selector: 'edge',
      style: {
        'width': 1,
        'line-color': graphColors.edgeColor,
        'target-arrow-color': graphColors.edgeColor,
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        'opacity': 0.6,
      },
    },
    // Base edges (non-provenance)
    {
      selector: 'edge.base-edge',
      style: {
        'width': 1,
        'line-color': graphColors.edgeColor,
        'target-arrow-color': graphColors.edgeColor,
        'opacity': 0.5,
      },
    },
    // Causal chain overlay edges (Phase F)
    {
      selector: 'edge.causal-chain-edge',
      style: {
        'width': 4,
        'line-color': '#9c27b0',
        'target-arrow-color': '#9c27b0',
        'target-arrow-shape': 'triangle',
        'source-arrow-shape': 'circle',
        'source-arrow-color': '#9c27b0',
        'curve-style': 'unbundled-bezier',
        'control-point-distances': [40],
        'control-point-weights': [0.5],
        'opacity': 0.9,
        'line-style': 'solid',
        'z-index': 999,
      },
    },
    // Provenance path highlighting - ORANGE (Phase F)
    {
      selector: 'edge.provenance-path',
      style: {
        'width': 4,
        'line-color': '#ff5722',
        'target-arrow-color': '#ff5722',
        'target-arrow-shape': 'triangle',
        'opacity': 1,
        'z-index': 998,
        'line-style': 'solid',
      },
    },
  ]

  // Find shortest path from any anchor to target node (BFS) - uses meta_ids
  const findPathFromAnchor = useCallback((targetMetaId: string): string[] => {
    if (!graphData || anchorIds.length === 0) return []
    if (anchorIds.includes(targetMetaId)) return [targetMetaId]

    // Build adjacency list using meta_ids
    const adjacency: Record<string, Set<string>> = {}
    for (const edge of graphData.edges || []) {
      const sourceNode = graphData.nodes.find((n: GraphNode) => n.id === edge.source)
      const targetNode = graphData.nodes.find((n: GraphNode) => n.id === edge.target)
      if (!sourceNode || !targetNode) continue

      const sourceMetaId = sourceNode.meta_id
      const targetMetaId = targetNode.meta_id

      if (!adjacency[sourceMetaId]) adjacency[sourceMetaId] = new Set()
      if (!adjacency[targetMetaId]) adjacency[targetMetaId] = new Set()
      adjacency[sourceMetaId].add(targetMetaId)
      adjacency[targetMetaId].add(sourceMetaId)
    }

    // BFS from anchors to target
    const visited = new Set<string>()
    const queue: [string, string[]][] = anchorIds.map(a => [a, [a]])

    while (queue.length > 0) {
      const [current, path] = queue.shift()!
      if (current === targetMetaId) return path

      if (visited.has(current)) continue
      visited.add(current)

      for (const neighbor of adjacency[current] || []) {
        if (!visited.has(neighbor)) {
          queue.push([neighbor, [...path, neighbor]])
        }
      }
    }

    return []
  }, [graphData, anchorIds])

  // Handle single-click: select node and show info
  const handleNodeSelect = useCallback((shapeId: string, nodeData: any) => {
    const metaId = nodeData.metaId || shapeId
    dispatch(setSelectedNode(metaId))
    const path = findPathFromAnchor(metaId)
    setHighlightedPath(path)
  }, [dispatch, findPathFromAnchor])

  // Handle double-click: open editor modal
  const handleNodeDoubleClick = useCallback((shapeId: string, nodeData: any) => {
    const metaId = nodeData.metaId || shapeId
    setEditingNode({
      id: metaId,
      label: nodeData.fullLabel || nodeData.label || metaId,
      description: nodeData.description
    })
    setEditorOpen(true)
  }, [])

  // Reset view to 100% zoom and scroll to center on Company Name node
  const handleResetView = useCallback(() => {
    if (!cyRef.current) return
    const cy = cyRef.current

    // Reset zoom to 100%
    cy.zoom(1.0)
    setZoomLevel(100)

    // Reset pan to the calculated offset (aligns graph with scroll coordinates)
    cy.pan({ x: canvasOffset.x, y: canvasOffset.y })

    // Scroll to center on Company Name node
    const scrollWrapper = document.querySelector('.cytoscape-scroll-wrapper')
    if (scrollWrapper) {
      const companyNodeShapeId = metaIdToShapeId.get(COMPANY_NAME_NODE_ID)
      if (companyNodeShapeId) {
        const companyNode = cy.getElementById(companyNodeShapeId)
        if (companyNode.length > 0) {
          const pos = companyNode.position()
          // Convert graph coordinates to scroll position (accounting for offset)
          const scrollLeft = pos.x + canvasOffset.x - scrollWrapper.clientWidth / 2
          const scrollTop = pos.y + canvasOffset.y - scrollWrapper.clientHeight / 2
          scrollWrapper.scrollTo({
            left: Math.max(0, scrollLeft),
            top: Math.max(0, scrollTop),
            behavior: 'smooth'
          })
          return
        }
      }

      // Fallback: scroll to center of canvas
      const scrollWidth = scrollWrapper.scrollWidth
      const scrollHeight = scrollWrapper.scrollHeight
      scrollWrapper.scrollTo({
        left: (scrollWidth - scrollWrapper.clientWidth) / 2,
        top: (scrollHeight - scrollWrapper.clientHeight) / 2,
        behavior: 'smooth'
      })
    }
  }, [metaIdToShapeId, canvasOffset])

  // Handle Cytoscape ready - only set up event listeners here
  // Initial view setup is done in a separate useEffect that waits for canvasOffset
  const handleCyReady = useCallback((cy: Core) => {
    // CRITICAL: react-cytoscapejs may call this with null during unmount
    // Don't clear cyRef.current if cy is null - we need it for saving viewport
    if (!cy) return

    cyRef.current = cy

    // Single-click: select node
    cy.on('tap', 'node', (event) => {
      const node = event.target as NodeSingular
      const nodeData = node.data()
      handleNodeSelect(node.id(), nodeData)
    })

    // Double-click: open editor
    cy.on('dbltap', 'node', (event) => {
      const node = event.target as NodeSingular
      const nodeData = node.data()
      handleNodeDoubleClick(node.id(), nodeData)
    })

    // Click on background: deselect
    cy.on('tap', (event) => {
      if (event.target === cy) {
        dispatch(setSelectedNode(null))
        setHighlightedPath([])
      }
    })

    // Track zoom level changes
    cy.on('zoom', () => {
      const currentZoom = cy.zoom()
      setZoomLevel(Math.round(currentZoom * 100))
    })

    // Mark Cytoscape as ready so the initial view effect can run
    setCyReady(true)
  }, [dispatch, handleNodeSelect, handleNodeDoubleClick])

  // Filter button handlers
  const handleFilterChange = (mode: GraphFilterMode) => {
    dispatch(setFilterMode(mode))
  }

  // Export graph as image (Phase F)
  const handleExportGraph = useCallback((format: 'png' | 'svg') => {
    if (!cyRef.current) return

    const cy = cyRef.current as any
    const timestamp = new Date().toISOString().slice(0, 10)
    const filename = `business-graph-${timestamp}.${format}`

    if (format === 'png') {
      const pngData = cy.png({
        output: 'blob',
        bg: 'white',
        scale: 2,
        full: true
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(pngData)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    } else if (format === 'svg') {
      const svgData = cy.svg({
        scale: 1,
        full: true,
        bg: 'white'
      })
      const blob = new Blob([svgData], { type: 'image/svg+xml' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    }
  }, [])

  // Calculate stats
  const totalNodes = graphData?.nodes?.length || 0
  const exploredCount = new Set([...anchorIds, ...exploredIds]).size
  const coveragePercent = totalNodes > 0 ? ((exploredCount / totalNodes) * 100).toFixed(1) : '0'

  // Get selected node data for info panel
  const selectedNodeData = useMemo(() => {
    if (!selectedNodeId || !graphData) return null
    return graphData.nodes.find((n: GraphNode) => n.meta_id === selectedNodeId)
  }, [selectedNodeId, graphData])

  if (loading) {
    return (
      <div className="business-graph-loading">
        <div className="loading-spinner" />
        <p>Loading Business Graph...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="business-graph-error">
        <p>Error loading graph: {error}</p>
        <button onClick={() => dispatch(loadGraphData())}>Retry</button>
      </div>
    )
  }

  if (!graphData) {
    return (
      <div className="business-graph-empty">
        <p>No graph data available</p>
      </div>
    )
  }

  return (
    <div className={`business-graph-container${isFullscreen ? ' fullscreen' : ''}`}>
      {/* Stats bar */}
      {showStats && (
        <div className="graph-stats-bar">
          <div className="stat-item">
            <span className="stat-label">TOTAL NODES</span>
            <span className="stat-value">{totalNodes}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">EXPLORED</span>
            <span className="stat-value">{exploredCount} ({coveragePercent}%)</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">ANCHORS</span>
            <span className="stat-value">{anchorIds.length}</span>
          </div>
          {confidenceScore > 0 && (
            <div className="stat-item">
              <span className="stat-label">CONFIDENCE</span>
              <span className="stat-value">{confidenceScore}%</span>
            </div>
          )}
        </div>
      )}

      {/* Filter controls */}
      {showFilters && (
        <div className="graph-filter-bar">
          <button
            className={`filter-btn ${filterMode === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filterMode === 'related' ? 'active' : ''}`}
            onClick={() => handleFilterChange('related')}
          >
            Related
          </button>
          <button
            className={`filter-btn ${filterMode === 'unrelated' ? 'active' : ''}`}
            onClick={() => handleFilterChange('unrelated')}
          >
            Unrelated
          </button>
          <button
            className={`filter-btn ${filterMode === 'company_info' ? 'active' : ''}`}
            onClick={() => handleFilterChange('company_info')}
            title="Double-click nodes to edit company-specific data"
          >
            Company Info
          </button>
          {/* Causal chain toggle */}
          <button
            className={`filter-btn ${showCausalChain ? 'active' : ''}`}
            onClick={() => {
              if (causalChainVisualization) {
                dispatch(toggleCausalChainOverlay())
              } else {
                setShowNoCausalChainAlert(true)
              }
            }}
            title="Toggle BSC causal chain visualization"
          >
            Causal Chain
          </button>
          {/* Export buttons separator */}
          <span className="filter-separator" />
          <button
            className="filter-btn filter-btn-export"
            onClick={() => handleExportGraph('png')}
            title="Export graph as PNG image"
          >
            Export PNG
          </button>
          <button
            className="filter-btn filter-btn-export"
            onClick={() => handleExportGraph('svg')}
            title="Export graph as SVG vector"
          >
            Export SVG
          </button>
          {/* Spacer to push Reset and Zoom to the right */}
          <span className="filter-spacer" />
          {/* Reset button */}
          <button
            className="filter-btn filter-btn-reset"
            onClick={handleResetView}
            title="Reset to 100% zoom centered on Company Name"
          >
            Reset
          </button>
          {/* Zoom controls */}
          <button
            className="filter-btn filter-btn-zoom"
            onClick={handleZoomOut}
            title="Zoom out"
            disabled={zoomLevel <= 50}
          >
            -
          </button>
          <span className="zoom-indicator">{zoomLevel}%</span>
          <button
            className="filter-btn filter-btn-zoom"
            onClick={handleZoomIn}
            title="Zoom in"
            disabled={zoomLevel >= 300}
          >
            +
          </button>
          <button
            className={`filter-btn graph-fullscreen-toggle${isFullscreen ? ' active' : ''}`}
            onClick={() => setIsFullscreen(prev => !prev)}
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand graph to fullscreen'}
          >
            {isFullscreen ? '⛶' : '⛶'}
          </button>
        </div>
      )}

      {/* Cytoscape graph with scroll bars */}
      <div className="cytoscape-scroll-wrapper" style={isFullscreen ? undefined : { height: `${height}px` }}>
        <div
          className="cytoscape-canvas"
          style={{
            width: `${canvasSize.width}px`,
            height: `${canvasSize.height}px`,
            minWidth: `${canvasSize.width}px`,
            minHeight: `${canvasSize.height}px`
          }}
        >
          <CytoscapeComponent
            elements={elements}
            stylesheet={stylesheet}
            style={{ width: '100%', height: '100%' }}
            cy={(cy: Core) => handleCyReady(cy)}
            layout={{ name: 'preset', fit: false }}
            wheelSensitivity={0.3}
            minZoom={0.5}
            maxZoom={3}
            boxSelectionEnabled={false}
            autounselectify={false}
            userPanningEnabled={true}
            userZoomingEnabled={false}
            // Cytoscape defaults (4px desktop / 8px touch) are too tight: natural
            // mouse drift between a pair of clicks easily exceeds 4px and drops
            // the dbltap to two separate taps, so the NodeEditorModal misses.
            desktopTapThreshold={12}
            touchTapThreshold={16}
          />
        </div>
      </div>

      {/* Legend */}
      <div className="graph-legend">
        <div className="legend-item">
          <span className="legend-dot anchor" />
          <span>Anchor</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot explored" />
          <span>Explored</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot unexplored" />
          <span>Unexplored</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot external" />
          <span>External</span>
        </div>
        {populatedNodes.size > 0 && (
          <div className="legend-item">
            <span className="legend-dot has-data" />
            <span>Company Data</span>
          </div>
        )}
        {showCausalChain && (
          <div className="legend-item">
            <span className="legend-line causal" />
            <span>BSC Causal Chain</span>
          </div>
        )}
        {highlightedPath.length > 1 && (
          <div className="legend-item">
            <span className="legend-line provenance" />
            <span>Path from Anchor</span>
          </div>
        )}
      </div>

      {/* Selected node info with provenance path */}
      {selectedNodeData && (
        <div className="selected-node-info">
          <h4>Selected Node</h4>
          <p><strong>ID:</strong> {selectedNodeData.meta_id}</p>
          <p><strong>Name:</strong> {selectedNodeData.label}</p>
          {/* Company-specific data (prioritized over generic description) */}
          {companyNodeLoading && (
            <p className="company-data-loading"><em>Loading company data...</em></p>
          )}
          {companyNodeValue && (
            <div className="company-node-value">
              <p><strong>Company Data:</strong></p>
              <p className="node-value-text">{companyNodeValue}</p>
            </div>
          )}
          {/* Ontology description (shown when no company data available) */}
          {!companyNodeValue && !companyNodeLoading && selectedNodeData.meta_description && (
            <p><strong>Description:</strong> {selectedNodeData.meta_description}</p>
          )}
          {highlightedPath.length > 1 && (
            <div className="provenance-path-info">
              <p><strong>Path from anchor ({highlightedPath.length} nodes):</strong></p>
              <p className="path-trail">
                {highlightedPath.map((nodeId, idx) => (
                  <span key={nodeId}>
                    {nodeId.replace(/_id$/, '').replace(/_/g, ' ')}
                    {idx < highlightedPath.length - 1 && ' -> '}
                  </span>
                ))}
              </p>
            </div>
          )}
          <p className="edit-hint">Double-click node to edit</p>
        </div>
      )}

      {/* Taxonomy distribution (if available) */}
      {Object.keys(taxonomyDistribution).length > 0 && (
        <div className="taxonomy-distribution">
          <h4>Taxonomy Coverage</h4>
          <div className="distribution-bars">
            {Object.entries(taxonomyDistribution).map(([taxonomy, count]) => (
              <div key={taxonomy} className="distribution-item">
                <span className="taxonomy-name">{taxonomy}</span>
                <span className="taxonomy-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Strategic Insights - Collapsible Panel */}
      {showInsights && insights && (Object.keys(insights.blind_spots || {}).length > 0 || insights.cross_pillar_dependencies?.length > 0 || insights.focus_drift_warning) && (
        <div className={`insights-panel ${insightsExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="insights-header" onClick={() => setInsightsExpanded(!insightsExpanded)}>
            <span className="insights-icon">💡</span>
            <span className="insights-title">Strategic Insights</span>
            <span className="insights-toggle">{insightsExpanded ? '▼' : '▶'}</span>
          </div>
          {insightsExpanded && (
            <div className="insights-content">
              {insights.blind_spots && Object.keys(insights.blind_spots).length > 0 && (
                <div className="insight-section">
                  <h5>Blind Spots</h5>
                  <ul>
                    {Object.entries(insights.blind_spots).slice(0, 5).map(([perspective, nodeIds], idx) => (
                      <li key={idx}>
                        <span className="insight-perspective">{perspective}: </span>
                        {(nodeIds as string[]).slice(0, 3).map((nodeId, nodeIdx) => (
                          <button
                            key={nodeIdx}
                            className="insight-node-btn"
                            onClick={() => {
                              // Dispatch triggers the selectedNodeId effect which handles scroll centering
                              dispatch(setSelectedNode(nodeId))
                            }}
                          >
                            {nodeId.replace(/_id$/, '').replace(/_/g, ' ')}
                          </button>
                        ))}
                        {(nodeIds as string[]).length > 3 && (
                          <span className="insight-more">+{(nodeIds as string[]).length - 3} more</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {insights.cross_pillar_dependencies && insights.cross_pillar_dependencies.length > 0 && (
                <div className="insight-section">
                  <h5>Cross-Pillar Dependencies</h5>
                  <ul>
                    {insights.cross_pillar_dependencies.map((dep: any, idx: number) => (
                      <li key={idx}>
                        Pillars {dep.pillars?.join(' & ')}: {dep.shared_nodes?.length || 0} shared nodes
                        {dep.insight && <span className="insight-reason"> - {dep.insight}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {insights.focus_drift_warning && (
                <div className="insight-section focus-drift">
                  <h5>Focus Drift Warning</h5>
                  <p>{insights.focus_drift_warning}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Node Editor Modal - opens on double-click */}
      {editingNode && (
        <NodeEditorModal
          isOpen={editorOpen}
          nodeId={editingNode.id}
          nodeLabel={editingNode.label}
          nodeDescription={editingNode.description}
          companyId={companyId}
          onClose={() => {
            setEditorOpen(false)
            setEditingNode(null)
          }}
          onSave={handleNodeSaved}
        />
      )}

      {/* No Causal Chain Alert */}
      {showNoCausalChainAlert && (
        <div className="no-causal-chain-overlay">
          <div className="no-causal-chain-alert">
            <p>No causal chain to display</p>
            <button onClick={() => setShowNoCausalChainAlert(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default BusinessGraphRenderer
